import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Percent } from "lucide-react";

interface EditCommissionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  mentorId: string;
  mentorName: string;
  currentRate?: number;
  onSuccess: () => void;
}

export const EditCommissionDialog: React.FC<EditCommissionDialogProps> = ({
  isOpen,
  onClose,
  mentorId,
  mentorName,
  currentRate = 20,
  onSuccess
}) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [commissionRate, setCommissionRate] = useState(currentRate.toString());

  useEffect(() => {
    setCommissionRate(currentRate.toString());
  }, [currentRate, isOpen]);

  const handleSave = async () => {
    try {
      setLoading(true);
      
      const rate = parseFloat(commissionRate);
      
      if (isNaN(rate) || rate < 0 || rate > 100) {
        toast({
          title: 'Erro',
          description: 'Taxa deve ser um número entre 0 e 100',
          variant: 'destructive',
        });
        return;
      }
      
      // Converter percentual para decimal (ex: 20% = 0.2)
      const rateDecimal = rate / 100;
      
      const { error } = await supabase
        .from('mentor_info')
        .update({ commission_rate: rateDecimal })
        .eq('profile_id', mentorId);
      
      if (error) {
        throw error;
      }
      
      toast({
        title: 'Sucesso',
        description: `Taxa de comissão atualizada para ${rate}%`,
      });
      
      onSuccess();
      onClose();
      
    } catch (error: any) {
      console.error('Error updating commission rate:', error);
      toast({
        title: 'Erro',
        description: error.message || 'Erro ao atualizar taxa de comissão',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setCommissionRate('20');
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => {}} modal>
      <DialogContent 
        className="bg-[#242C37] border-[#2A3749] text-white"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Editar Taxa de Comissão</DialogTitle>
          <DialogDescription className="text-gray-400">
            Configurar taxa de comissão personalizada para <strong>{mentorName}</strong>.
            <br />
            Taxa padrão da plataforma: 20%
          </DialogDescription>
        </DialogHeader>
        
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="commission_rate">Taxa de Comissão (%)</Label>
            <div className="relative">
              <Input
                id="commission_rate"
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={commissionRate}
                onChange={(e) => setCommissionRate(e.target.value)}
                className="bg-[#1E293B] border-[#2A3749] text-white pr-10"
                placeholder="20.0"
              />
              <Percent className="absolute right-3 top-3 h-4 w-4 text-gray-400" />
            </div>
            <p className="text-sm text-gray-400">
              A plataforma ficará com {commissionRate || 0}% de cada venda deste mentor
            </p>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleReset}
              className="bg-transparent border-[#2A3749] text-gray-400 hover:bg-[#2A3749] hover:text-white"
            >
              Usar Padrão (20%)
            </Button>
          </div>
        </div>
        
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            className="bg-transparent border-[#2A3749] text-gray-400 hover:bg-[#2A3749] hover:text-white"
          >
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={loading}
            className="bg-[#00B6F0] hover:bg-[#00A3D9] text-white"
          >
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}; 